import {
  defaultShouldDehydrateQuery,
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/nextjs";
import SuperJSON from "superjson";

interface HttpishError {
  status?: number;
  network?: boolean;
  /** Route template set by apiFetch (no ids, no query string). */
  route?: string;
}

/**
 * Default retry policy: never retry 4xx (incl. 429 — retrying a rate limit or a
 * 403/404 only adds load and delays the error), cap 5xx/network at 2 retries.
 * (Retry-After is not surfaced by apiFetch's errors, so 429 simply isn't retried.)
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = (error as HttpishError | null)?.status;
  if (typeof status === "number" && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

// Report each distinct network/5xx failure (kind + route) at most once a minute:
// the backend being down fails every query at once.
const lastReported = new Map<string, number>();
const REPORT_WINDOW_MS = 60_000;
const MAX_TRACKED = 200;

/** Report only network failures and 5xx (4xx is the user's problem, not ours). */
export function reportQueryError(error: unknown): void {
  const e = error as (HttpishError & Error) | null;
  if (!e) return;
  const isServer = typeof e.status === "number" && e.status >= 500;
  if (!e.network && !isServer) return;
  // The user's own connection being down is not our incident.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const kind = e.network ? "network" : "5xx";
  const route = e.route ?? "unknown";
  const key = `${kind}:${route}`;
  const now = Date.now();
  if (now - (lastReported.get(key) ?? 0) < REPORT_WINDOW_MS) return;
  if (lastReported.size >= MAX_TRACKED) lastReported.clear();
  lastReported.set(key, now);
  // A fixed message: the original may hold a server `detail` (names, emails,
  // org ids), so neither it nor the response body is sent. Status class + route
  // template only; the fingerprint groups by the same two.
  Sentry.captureException(new Error(`API ${kind}`), {
    tags: { source: "react-query", status_class: kind, route },
    fingerprint: ["react-query", kind, route],
    extra: { status: e.status ?? null },
  });
}

export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({ onError: reportQueryError }),
    mutationCache: new MutationCache({ onError: reportQueryError }),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 30 * 1000,
      },
      dehydrate: {
        serializeData: SuperJSON.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
      hydrate: {
        deserializeData: SuperJSON.deserialize,
      },
    },
  });
